import { Asset } from '../../../models/asset.model';
import { AssetType } from '../../../models/enums/asset-type-enum';
import { LoanPurpose } from '../../../models/enums/loan-purpose.enum';
import { MortgageApplication } from '../../../models/mortgage-app.model';
import { SubjectProperty } from '../../../models/subject-property.model';
import { activeOnly } from '../../../utils/entity-state';

/** `PurchaseCreditSourceType` value the Loan Type step renders as "Gift". */
export const GIFT_FUNDS_DOWN_PAYMENT_SOURCE = 'GiftFunds';

/**
 * LLS-641 - commit-time rule for the Cash Gift "used for a down payment?" answer.
 *
 * Runs once per successful single-asset Save, on the asset that is about to be
 * emitted to the list:
 *  - Not a Cash Gift: the answer is meaningless, so it is cleared. Otherwise a
 *    "Yes" given while the type was Cash Gift would ride along after the borrower
 *    retypes the asset to, say, a checking account.
 *  - Cash Gift + Yes: the mortgage's Down Payment Source becomes Gift, so the
 *    Loan Type step shows it pre-selected. Same mortgage object the assets step
 *    saves on Next, so the asset flag and the source persist in one save.
 *    The gift's URLA 4d "Appl. to Down Payment" is filled with what the gift
 *    has left after closing costs. An amount already there (e.g. typed by the
 *    loan officer) is kept unless it no longer fits under the gift amount.
 *  - Cash Gift + No / unanswered: the Down Payment Source is left exactly as
 *    it is (spec: "No" must not modify it).
 *
 * Deliberately NOT gated on loan purpose or the co-apply flow: the spec ties
 * the behavior to the asset type alone.
 */
export function applyCashGiftDownPaymentSource(mortgage: MortgageApplication, asset: Asset): void {
  if (asset.assetType !== AssetType.CashGift) {
    asset.isDownPaymentSource = undefined;
    return;
  }
  if (asset.isDownPaymentSource !== true) {
    return;
  }
  if (!mortgage.subjectProperty) {
    mortgage.subjectProperty = new SubjectProperty();
  }
  mortgage.subjectProperty.downPaymentSource = GIFT_FUNDS_DOWN_PAYMENT_SOURCE;
  applyGiftAmountToDownPayment(asset);
}

/**
 * Total of the Cash Gifts the borrower said are used for the down payment, or
 * undefined when there are none. The Loan Type step uses it as a starting
 * Down Payment amount; the borrower can still change it (savings on top of a
 * gift are common, so it is a floor, not the answer).
 */
export function cashGiftDownPaymentTotal(assets: Asset[] | undefined | null): number | undefined {
  const total = activeOnly(assets)
    .filter(a => a.assetType === AssetType.CashGift && a.isDownPaymentSource === true)
    .reduce((sum, a) => sum + (a.cashMarketValue ?? 0), 0);
  return total > 0 ? total : undefined;
}

/**
 * Down Payment for the Loan Type step to start at: the Cash Gift total, whenever
 * the current down payment (derived from the saved loan amount, if any) is below
 * it. A larger down payment is kept - it is the gift plus the borrower's own funds.
 * Returns undefined when the current down payment should stay. Not in co-apply
 * (the inputs are read-only there) or on a refinance (no down payment input).
 */
export function initialCashGiftDownPayment(
  mortgage: MortgageApplication,
  inCoApplyFlow: boolean,
  currentDownPayment: number | undefined
): number | undefined {
  if (inCoApplyFlow || mortgage.subjectProperty?.purposeOfLoan === LoanPurpose.Refinance) {
    return undefined;
  }
  const giftTotal = cashGiftDownPaymentTotal(mortgage.assets);
  return giftTotal !== undefined && giftTotal > (currentDownPayment ?? 0) ? giftTotal : undefined;
}

// Admin's Gifts or Grants grid requires closing costs + down payment <= cash/market value.
function applyGiftAmountToDownPayment(asset: Asset): void {
  if (!asset.cashMarketValue) {
    return;
  }
  const available = Math.max(asset.cashMarketValue - (asset.amountAppliedToClosingCosts ?? 0), 0);
  if (asset.amountAppliedToDownPayment == null || asset.amountAppliedToDownPayment > available) {
    asset.amountAppliedToDownPayment = available;
  }
}
